import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { LedgerCloseSettingsUpdate } from '../../interfaces/accounting.interface';
import { LedgerCloseSettingsService } from '../../services/ledger-close-settings.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const LedgerCloseSettings: React.FC = () => {
    const toast = useToast();

    // Must match the backend guard on PUT (LEDGER_CLOSE_MANAGE); gating on a different key showed
    // an enabled Save button to users the server then refused with a 403.
    const canManage = usePermission(PERMISSIONS.LEDGER_CLOSE_MANAGE);
    const isReadOnly = !canManage;
    // B4-F: the amount threshold also needs ACCOUNT_MANAGE on the server.
    const canSetThreshold = usePermission(PERMISSIONS.ACCOUNT_MANAGE);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // closedThrough is stored as "YYYY-MM-DD" or "" (empty string = no lock)
    const [closedThrough, setClosedThrough] = useState<string>('');
    const [requireSeparatePoster, setRequireSeparatePoster] = useState<boolean>(false);
    // B4-F: text so a blank field means "no threshold"
    const [separatePosterAbove, setSeparatePosterAbove] = useState<string>('');

    useEffect(() => {
        const loadSettings = async () => {
            setLoading(true);
            try {
                const data = await LedgerCloseSettingsService.get();
                setClosedThrough(data.closedThrough ?? '');
                setRequireSeparatePoster(!!data.requireSeparatePoster);
                setSeparatePosterAbove(data.separatePosterAbove ? String(data.separatePosterAbove) : '');
            } catch (err) {
                toast.showApiError(err, 'Failed to load close-books settings');
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const dto: LedgerCloseSettingsUpdate = {
                closedThrough: closedThrough || null,
                requireSeparatePoster,
                // B4-F: blank clears (the API reads 0 as "clear"); only sent by a caller who may change it
                ...(canSetThreshold ? { separatePosterAbove: separatePosterAbove.trim() === '' ? 0 : Number(separatePosterAbove) } : {}),
            };
            await LedgerCloseSettingsService.update(dto);
            toast.showSuccess('Close-books setting saved');
        } catch (err) {
            toast.showApiError(err, 'Failed to save close-books setting');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async () => {
        setSaving(true);
        try {
            await LedgerCloseSettingsService.update({ closedThrough: null, requireSeparatePoster });
            setClosedThrough('');
            toast.showSuccess('Posting-period lock cleared');
        } catch (err) {
            toast.showApiError(err, 'Failed to clear posting-period lock');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box data-help-page="accounting.ledgerClose.page" sx={{ padding: '24px', maxWidth: 640 }}>
            <Typography variant="h5" gutterBottom>
                Close Books Through
            </Typography>

            {isReadOnly && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have read-only access. Changing the close date needs the "Close and reopen periods"
                    permission.
                </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Any journal posting dated on or before the closed-through date will be blocked. Leave the
                date blank to remove the lock and allow postings to any open period.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                The lock stays in place until someone with this permission changes or clears it. After a
                month-end or year-end close, set it to the last day of the closed period so nothing can be
                posted back into it.
            </Alert>

            {closedThrough && closedThrough >= toLocalIsoDate() && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    This date is today or later, so it also blocks postings dated today — sales invoices,
                    bills, payroll and every other module posting — until the lock is moved back.
                </Alert>
            )}

            {/* Closed Through Date */}
            <TextField data-help="accounting.ledgerClose.field.closedThrough"
                label="Closed Through"
                type="date"
                value={closedThrough}
                onChange={(e) => setClosedThrough(e.target.value)}
                disabled={isReadOnly}
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="normal"
                helperText={
                    closedThrough
                        ? `Postings dated on or before ${formatDate(closedThrough)} are blocked.`
                        : 'No lock active — all periods are open.'
                }
            />

            {/* Maker-checker */}
            <Box sx={{ mt: 2 }}>
                <FormControlLabel data-help="accounting.ledgerClose.field.separatePoster"
                    control={
                        <Switch
                            checked={requireSeparatePoster}
                            onChange={(e) => setRequireSeparatePoster(e.target.checked)}
                            disabled={isReadOnly}
                            inputProps={{ 'aria-label': 'Require a different user to post a journal' }}
                        />
                    }
                    label="Require a different user to post a journal than the one who created it"
                />
                <Typography variant="body2" color="text.secondary">
                    When on, you cannot post your own draft journal: someone else with the post permission
                    must do it. Leave off if one person keeps the books.
                </Typography>
                {/* ---- B4-F ---- */}
                <TextField data-help="accounting.ledgerClose.field.threshold"
                    label="Different poster required above (amount)"
                    type="number"
                    value={separatePosterAbove}
                    onChange={(e) => setSeparatePosterAbove(e.target.value)}
                    disabled={isReadOnly || !canSetThreshold || requireSeparatePoster}
                    inputProps={{ min: 0, 'aria-label': 'Separate poster threshold' }}
                    margin="normal"
                    sx={{ width: '100%', maxWidth: 360 }}
                    helperText={requireSeparatePoster
                        ? 'Not needed: every journal already needs a different poster.'
                        : 'Journals whose total is above this amount cannot be posted by the user who created them. Leave blank for no limit. Needs the "Manage chart of accounts" permission.'}
                />
                {/* ---- end B4-F ---- */}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button data-help="accounting.ledgerClose.save"
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isReadOnly || saving}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button data-help="accounting.ledgerClose.clear"
                    variant="outlined"
                    color="warning"
                    onClick={handleClear}
                    disabled={isReadOnly || saving || !closedThrough}
                >
                    Clear Lock
                </Button>
            </Box>
        </Box>
    );
};

export default LedgerCloseSettings;
